<script>
import {mapActions, mapMutations, mapState} from "vuex";
import SurveyLanguageSelector from "../surveys/components/SurveyLanguageSelector.vue";
import SurveyClipboardButton from "../surveys/components/SurveyClipboardButton.vue";

export default {
    name: "PollForm",
    components: {SurveyClipboardButton, SurveyLanguageSelector},
    data() {
        return {
            mode: 'create',
            pollInfoValid: false,
            nameRules: [
                v => !!v || 'Le nom est requis',
            ],
            surveyRules: [
                v => !!v || 'Le questionnaire est requis',
            ],
            deleteDialog: false,
        }
    },
    created() {
        if (this.$route.params.id) {
            this.mode = "edit"
            this.setSelectedPollId(this.$route.params.id)
            this.getPollInfo()
        }
    },
    mounted() {
        this.getCreateFormOptions()
        if(this.mode === 'create'){
            this.initPollForm()
        }
        this.setSelectedPatients([])
    },
    computed: {
        isEdit () {
            return this.mode === 'edit'
        },
        name: {
            get() {
                return this.$store.state.polls.form.name
            },
            set(value) {
                this.setFormName(value)
            }
        },

        nameOriginal() {
            return this.$store.state.polls.form.nameOriginal
        },

        punctualScore: {
            get() {
                return this.$store.state.polls.form.score_id
            },
            set(value) {
                this.setFormScoreId(value)
            }
        },

        selectedPatients: {
            get() {
                return this.$store.state.polls.selectedPatients
            },
            set(value) {
                this.setSelectedPatients(value)
            }
        },

        patientIds: {
            get() {
                return this.$store.state.polls.form.patient_ids
            },
            set(value) {
                this.setFormPatientIds(value)
            }
        },

        searchPatientName: {
            get() {
                return this.$store.state.polls.searchPatient
            },
            set(value) {
                this.setSearchPatientName(value)
                this.searchPatient()
            }
        },

        ...mapState('polls', {
            selectedPoll: state => state.selectedPoll,
            patientsList: state => state.patientsList,
            alreadySelectedPatients: state => state.selectedPoll.patients
        }),

        ...mapState('surveys', {
            punctualScoreList: state => state.active_punctual_scores,
            language: state => state.currentLanguage,
        }),
    },
    methods: {
        ...mapActions('polls', [
            'createPoll',
            'removeAllPatients',
            'selectAllPatients',
            'getCreateFormOptions',
            'searchPatient',
            'getPollInfo',
            'updatePoll',
            'restorePoll',
            'archivePoll',
        ]),
        ...mapMutations('polls', [
            'setFormName',
            'setFormScoreId',
            'setFormPatientIds',
            'setSelectedPatients',
            'selectPatient',
            'removePatient',
            'setSearchPatientName',
            'initPollForm',
            'setSelectedPollId'
        ]),

        validate() {
            if (this.$refs.pollForm.validate()) {
                if (this.mode === 'create') {
                    this.createPoll().finally(() => {
                        this.$router.replace({name: 'polls'})
                    })
                } else {
                    this.updatePoll().finally(() => {
                        this.$router.replace({name: 'polls'})
                    })
                }
            }
        },

        deletePoll() {
            if(this.mode === 'create') {
                this.$router.replace({name: 'polls'})
            }
        },
    },
    watch: {
        language() {
            if (this.$route.params.id) {
                this.getPollInfo()
            }
        }
    }
}
</script>

<template>
    <v-card class="patient fill-height">
        <v-container>
            <v-form ref="pollForm" v-model="pollInfoValid" lazy-validation>
                <v-layout>
                    <v-flex md10 offset-md1>
                        <v-layout row class="justify-space-between">
                            <h3 class="headline mb-4">Informations sur l'enquête</h3>
                            <survey-language-selector/>
                        </v-layout>
                        <v-layout row class="align-items-center">
                            <v-text-field
                                v-model="name"
                                :rules="nameRules"
                                label="Nom de l'enquête"
                                :disabled="!!selectedPoll.deleted_at"
                                required>
                            </v-text-field>
                            <SurveyClipboardButton :text-original="nameOriginal"/>
                        </v-layout>
                        <v-select
                            :items="punctualScoreList"
                            v-model="punctualScore" label="Questionnaire"
                            :rules="surveyRules"
                            item-text="name"
                            item-value="id"
                            :disabled="mode === 'edit' || !!selectedPoll.deleted_at"
                        ></v-select>
                    </v-flex>
                </v-layout>
                <v-layout>
                    <v-flex md10 offset-md1 text-xs-right>
                        <v-btn right color="warning" @click="restorePoll(selectedPoll.id)" v-if="selectedPoll.deleted_at">
                            <v-icon left>restore</v-icon>
                            Restaurer
                        </v-btn>
                        <v-btn right color="warning" @click="archivePoll(selectedPoll.id)" v-if="!selectedPoll.deleted_at && isEdit">
                            <v-icon left>archive</v-icon>
                            Archiver
                        </v-btn>
                        <v-btn :disabled="!pollInfoValid" color="success" @click="validate" v-if="!selectedPoll.deleted_at">
                            Envoyer <v-icon right>send</v-icon>
                        </v-btn>

                        <v-btn color="danger" flat @click="deleteDialog = true" v-if="!isEdit">
                            <v-icon>delete</v-icon> Annuler
                        </v-btn>
                    </v-flex>
                </v-layout>
                <v-layout>
                    <v-flex md10 offset-md1>
                        <h3 class="headline mb-4">Patients</h3>
                        <v-layout justify-space-between row align-center>
                            <v-flex xs5>
                                <h4 class="subheading">Liste des patients</h4>
                            </v-flex>
                            <v-flex xs2>
                                <v-layout row justify-center>
                                    <v-btn flat icon color="primary" @click="removeAllPatients" :disabled="isEdit">
                                        <v-icon>keyboard_double_arrow_left</v-icon>
                                    </v-btn>
                                    <v-btn flat icon color="primary" @click="selectAllPatients" :disabled="!!selectedPoll.deleted_at">
                                        <v-icon>keyboard_double_arrow_right</v-icon>
                                    </v-btn>
                                </v-layout>
                            </v-flex>
                            <v-flex xs5 text-xs-right>
                                <h4 class="subheading">Patients sélectionnés</h4>
                            </v-flex>
                        </v-layout>
                        <v-text-field
                            v-model="searchPatientName"
                            append-icon="search"
                            label="Rechercher un patient"
                            single-line
                            hide-details
                        ></v-text-field>
                        <v-layout justify-center row>
                            <v-flex class="border-light" xs6 p-2>
                                <v-list v-if="!selectedPoll.deleted_at">
                                    <template v-for="(patient, index) in patientsList">
                                        <v-list-tile :key="patient.id" @click="selectPatient(patient)" v-if="!patient.hide">
                                            <v-list-tile-content>
                                                <v-list-tile-title>{{ patient.name }}</v-list-tile-title>
                                            </v-list-tile-content>
                                            <v-list-tile-action>
                                                <v-icon>add</v-icon>
                                            </v-list-tile-action>
                                        </v-list-tile>
                                        <v-divider
                                            v-if="index + 1 < patientsList.length && !patientsList[index + 1].hide"
                                            :key="'divider_' + index"
                                        ></v-divider>
                                    </template>
                                </v-list>
                            </v-flex>
                            <v-flex class="border-primary" xs6 p-2>
                                <v-list>
                                    <template v-for="(patient, index) in selectedPatients" >
                                        <v-list-tile :key="patient.id" @click="removePatient(patient)" v-if="!patient.hide" :disabled="!!selectedPoll.deleted_at">
                                            <v-list-tile-content>
                                                <v-list-tile-title>{{ patient.name }}</v-list-tile-title>
                                            </v-list-tile-content>
                                            <v-list-tile-action>
                                                <v-icon>remove</v-icon>
                                            </v-list-tile-action>
                                        </v-list-tile>
                                        <v-divider
                                            v-if="index + 1 < selectedPatients.length"
                                            :key="'divider123_' + index"
                                        ></v-divider>
                                    </template>
                                    <template v-for="(patient, index) in alreadySelectedPatients" >
                                        <v-list-tile :key="patient.id" v-if="!patient.hide" @click="removePatient(patient)" :disabled="!!selectedPoll.deleted_at">
                                            <v-list-tile-content>
                                                <v-list-tile-title>{{ patient.name }} (Déjà envoyée)</v-list-tile-title>
                                            </v-list-tile-content>
                                            <v-list-tile-action>
                                                <v-icon>remove</v-icon>
                                            </v-list-tile-action>
                                        </v-list-tile>
                                        <v-divider
                                            v-if="index + 1 < selectedPatients.length"
                                            :key="'divider111_' + index"
                                        ></v-divider>
                                    </template>
                                </v-list>
                            </v-flex>
                        </v-layout>
                    </v-flex>
                </v-layout>
            </v-form>
            <v-dialog v-model="deleteDialog" persistent max-width="480">
                <v-card>
                    <v-card-title class="headline">Annuler l'enquête</v-card-title>
                    <v-card-text>Voulez-vous vraiment annuler cette enquête ?
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="secondary" text @click="deleteDialog = false">Non</v-btn>
                        <v-btn color="primary" text @click="deletePoll()">Oui, annuler</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-container>
    </v-card>
</template>

